import * as React from 'react'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import { X } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'

/**
 * Popover — a floating panel anchored to a trigger (Radix-backed), from the
 * Figma DS2026 "Popover" specs. Opens on click and can hold *interactive*
 * content: a heading, copy, form fields, action buttons. For a non-interactive
 * hover label use `Tooltip`; for a list of actions use `DropdownMenu`.
 *
 *   <Popover>
 *     <PopoverTrigger asChild><Button>Open</Button></PopoverTrigger>
 *     <PopoverContent size="sm">
 *       <PopoverClose />
 *       <p className="text-fbui-text-title font-semibold">Title</p>
 *       <p className="mt-1 text-fbui-text-secondary">Some details…</p>
 *     </PopoverContent>
 *   </Popover>
 *
 * Tokens (Figma → repo): white panel, gray-200 border, `shadow-fbui-lg`,
 * `rounded-xl` — the same surface as `DropdownMenu`/`Select`. `size` scales the
 * panel (`sm` = 240px / p-3 / 12px text, `md` = 288px / p-4 / 14px text); pass a
 * `className` width to override. Radix handles outside-dismiss, Escape, focus
 * trapping, and `side`/`align` placement (top · bottom · left · right).
 *
 * No arrow by default — a popover is anchored to the element you just clicked,
 * so it reads fine without one (and an arrow against the bordered white panel
 * leaves a seam). Pass `showArrow` if you want a plain white pointer.
 *
 * For a long list that should scroll, wrap the list in `PopoverScroll` (an inner
 * `max-height` + `overflow-y-auto` region) rather than putting `overflow` on
 * `PopoverContent` — `overflow` on the content box would clip it.
 */

export const Popover = PopoverPrimitive.Root
export const PopoverTrigger = PopoverPrimitive.Trigger
/** Position the popover relative to a custom element instead of the trigger. */
export const PopoverAnchor = PopoverPrimitive.Anchor

type PopoverSize = 'sm' | 'md'

/** Per-size default width, padding, and base text. */
const popoverSize: Record<PopoverSize, string> = {
  sm: 'w-60 p-3 text-xs',
  md: 'w-72 p-4 text-sm',
}

export interface PopoverContentProps extends React.ComponentPropsWithoutRef<
  typeof PopoverPrimitive.Content
> {
  /** Compact (`sm`) vs. roomier (`md`) panel. */
  size?: PopoverSize
  /** Render a plain white pointer toward the anchor. Off by default. */
  showArrow?: boolean
}

export const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  PopoverContentProps
>(
  (
    {
      className,
      size = 'md',
      showArrow = false,
      sideOffset = 8,
      children,
      ...props
    },
    ref
  ) => (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          'z-50 rounded-xl border border-fbui-gray-200 bg-fbui-base-white text-fbui-text-body shadow-fbui-lg',
          'origin-[--radix-popover-content-transform-origin]',
          popoverSize[size],
          className
        )}
        {...props}
      >
        {children}
        {showArrow && (
          <PopoverPrimitive.Arrow
            width={12}
            height={6}
            className="fill-fbui-base-white"
          />
        )}
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  )
)
PopoverContent.displayName = PopoverPrimitive.Content.displayName

export type PopoverCloseProps = React.ComponentPropsWithoutRef<
  typeof PopoverPrimitive.Close
>

/**
 * Dismiss button — defaults to a small ✕ pinned to the panel's top-right
 * corner. Pass `asChild` to wrap your own element instead.
 */
export const PopoverClose = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Close>,
  PopoverCloseProps
>(({ className, children, ...props }, ref) =>
  props.asChild ? (
    <PopoverPrimitive.Close ref={ref} className={className} {...props}>
      {children}
    </PopoverPrimitive.Close>
  ) : (
    <PopoverPrimitive.Close
      ref={ref}
      aria-label="Close"
      className={cn(
        'absolute right-2.5 top-2.5 inline-flex size-6 items-center justify-center rounded-md text-fbui-gray-400 outline-none transition-colors hover:bg-fbui-gray-100 hover:text-fbui-gray-600 focus-visible:ring-2 focus-visible:ring-fbui-ring-brand',
        className
      )}
      {...props}
    >
      <X weight="bold" className="size-3.5" aria-hidden="true" />
    </PopoverPrimitive.Close>
  )
)
PopoverClose.displayName = PopoverPrimitive.Close.displayName

export interface PopoverScrollProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Max height before the body scrolls. A number is treated as px. */
  maxHeight?: number | string
}

/**
 * Scrolling body region for a long list inside a `PopoverContent`. Keep any
 * pinned chrome (a heading, a `PopoverClose`) as siblings *outside* this so only
 * the list scrolls — and so the arrow (a sibling of the content) isn't clipped.
 */
export const PopoverScroll = React.forwardRef<
  HTMLDivElement,
  PopoverScrollProps
>(({ className, maxHeight = '15rem', style, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('fbui-scroll -mr-1 overflow-y-auto pr-1', className)}
    style={{ maxHeight, ...style }}
    {...props}
  />
))
PopoverScroll.displayName = 'PopoverScroll'
